import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	Outlet,
	useNavigate,
	useParams,
	useRouterState,
} from "@tanstack/react-router";
import {
	Calendar,
	CalendarCheck01,
	Hash01,
	Minimize01,
} from "@untitledui/icons";
import { useCallback, useMemo, useState } from "react";
import {
	DetailsState,
	DetailsSurface,
} from "@/components/Organisms/Details/Details";
import {
	PageHeader,
	type PageHeaderTab,
} from "@/components/Organisms/PageHeader/PageHeader";
import { PageLayout } from "@/components/Organisms/PageLayout/PageLayout";
import { Sidebar } from "@/components/Organisms/Sidebar/Sidebar";
import {
	AssetDetailsTab,
	AssetEntityDetails,
} from "@/features/assets/components";
import {
	AssetFormSaveProvider,
	useAssetFormSaveRegistry,
} from "@/features/assets/components/asset-form-save-context";
import { useAssetFormNavigationGuard } from "@/features/assets/hooks/use-asset-form-navigation-guard";
import { getAssetDisplayTitle } from "@/features/assets/lib/get-asset-display-title.util";
import {
	commitAutosaveOrStay,
	useJobDetailsSaveFailLabels,
} from "@/features/jobs/hooks/use-job-details-navigation-guard";
import {
	AssetWorkspaceTab,
	type AssetWorkspaceTabId,
	getAssetWorkspaceTabId,
} from "@/features/jobs/lib/get-asset-workspace-tab-id.util";
import {
	getRecordScopedAssetDrawerDetailsPath,
	getRecordScopedAssetWorkspacePath,
} from "@/features/records/lib/get-record-scoped-asset-path.util";
import { isNestedRecordUnderRecordHostAssetWorkspacePath } from "@/features/records/lib/is-nested-asset-under-record-workspace-path.util";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { assetInstanceQueryOptions } from "@/services/asset";
import { recordInstanceQueryOptions } from "@/services/record";
import { cx } from "@/utils/cx";
import {
	createdMetadataLabel,
	updatedMetadataLabel,
} from "@/utils/entity-metadata-date-label";

/**
 * Nested Asset workspace shell — record-context breadcrumbs and asset tabs.
 * Delete is not available from the record Assets tab (ADO #1564).
 */
export const Route = createFileRoute(
	"/_authenticated/(web-app)/_web/web/$orgId/records/(record)/$recordId/workspace/assets/$assetId/workspace",
)({
	component: AssetWorkspaceLayout,
});

function AssetWorkspaceLayout() {
	const pathname = useRouterState({
		select: (state) => state.location.pathname,
	});

	if (isNestedRecordUnderRecordHostAssetWorkspacePath(pathname)) {
		return <Outlet />;
	}

	return (
		<AssetFormSaveProvider>
			<AssetWorkspaceLayoutInner />
		</AssetFormSaveProvider>
	);
}

function AssetWorkspaceLayoutInner() {
	const { t } = useAppTranslation();
	const navigate = useNavigate();
	const params = useParams({
		from: "/_authenticated/(web-app)/_web/web/$orgId/records/(record)/$recordId/workspace/assets/$assetId/workspace",
	});
	const pathname = useRouterState({
		select: (state) => state.location.pathname,
	});
	const selectedTabId = getAssetWorkspaceTabId(pathname);
	const [isSidebarOpen, setIsSidebarOpen] = useState(true);

	const formSaveRegistry = useAssetFormSaveRegistry();
	const saveFailLabels = useJobDetailsSaveFailLabels();
	useAssetFormNavigationGuard();

	const runWithFormCommit = useCallback(
		async (action: () => void) => {
			const ctrl = formSaveRegistry?.getController() ?? null;
			if (ctrl?.hasPending()) {
				const shouldStay = await commitAutosaveOrStay(
					ctrl.commitPending,
					saveFailLabels,
				);
				if (shouldStay) return;
			}
			action();
		},
		[formSaveRegistry, saveFailLabels],
	);

	const assetQuery = useQuery(
		assetInstanceQueryOptions.detail(params.assetId, {
			organizationId: params.orgId,
		}),
	);

	const recordById = useQuery(
		recordInstanceQueryOptions.detail(params.recordId, {
			organizationId: params.orgId,
		}),
	);

	const title = getAssetDisplayTitle({
		name: assetQuery.data?.name,
		assetNumber: assetQuery.data?.assetNumber,
	});

	const recordNumberLabel =
		recordById.data?.recordNumber != null
			? t("assets.breadcrumb.recordWithNumber", {
					number: recordById.data.recordNumber,
				})
			: t("main.nav.items.records.label");

	const tabs: PageHeaderTab[] = useMemo(
		() => [
			{
				id: AssetWorkspaceTab.Form,
				label: t("jobs.landing.sections.form"),
			},
			{
				id: AssetWorkspaceTab.Records,
				label: t("main.jobs.workspace.tabs.records"),
			},
			{
				id: AssetWorkspaceTab.Attachments,
				label: t("jobs.landing.sections.attachments"),
			},
			{
				id: AssetWorkspaceTab.Notes,
				label: t("jobs.landing.sections.notes"),
			},
			{
				id: AssetWorkspaceTab.Map,
				label: t("jobs.landing.sections.map"),
			},
		],
		[t],
	);

	const metadata = useMemo(() => {
		const asset = assetQuery.data;
		if (!asset) return [];

		const items: {
			label: string;
			icon: typeof Hash01;
		}[] = [];

		if (asset.assetNumber != null) {
			items.push({
				label: String(asset.assetNumber),
				icon: Hash01,
			});
		}
		if (asset.createdAt) {
			items.push({
				label: createdMetadataLabel(t, asset.createdAt),
				icon: Calendar,
			});
		}
		if (asset.updatedAt) {
			items.push({
				label: updatedMetadataLabel(t, asset.updatedAt),
				icon: CalendarCheck01,
			});
		}
		return items;
	}, [assetQuery.data, t]);

	const handleBreadcrumbRecords = () => {
		void runWithFormCommit(() => {
			navigate({
				to: "/web/$orgId/records",
				params: { orgId: params.orgId },
				search: {},
			});
		});
	};

	const handleBreadcrumbRecord = () => {
		void runWithFormCommit(() => {
			navigate({
				to: "/web/$orgId/records/$recordId/workspace/form",
				params: { orgId: params.orgId, recordId: params.recordId },
			});
		});
	};

	const handleBreadcrumbAssets = () => {
		void runWithFormCommit(() => {
			navigate({
				href: `/web/${params.orgId}/records/${params.recordId}/workspace/assets`,
			});
		});
	};

	const handleCollapseWorkspace = () => {
		void runWithFormCommit(() => {
			navigate({
				href: getRecordScopedAssetDrawerDetailsPath({
					orgId: params.orgId,
					recordId: params.recordId,
					assetId: params.assetId,
				}),
			});
		});
	};

	const handleTabChange = (tabId: string) => {
		const next = tabId as AssetWorkspaceTabId;
		if (next === selectedTabId) return;

		void runWithFormCommit(() => {
			navigate({
				href: getRecordScopedAssetWorkspacePath({
					orgId: params.orgId,
					recordId: params.recordId,
					assetId: params.assetId,
					tab: next,
				}),
			});
		});
	};

	const sidebarLabel = t("main.jobs.workspace.assets.workspace.sidebarLabel");

	const sidebarTabs = useMemo(
		() => [
			{
				label: t("main.jobs.workspace.assets.drawer.tabs.details.label"),
				id: AssetDetailsTab.Details,
			},
			{
				label: t("main.jobs.workspace.assets.drawer.tabs.activity.label"),
				id: AssetDetailsTab.Activity,
			},
		],
		[t],
	);

	return (
		<div className="relative flex min-h-0 w-full flex-1 overflow-hidden">
			<div
				className={cx(
					"flex min-h-0 min-w-0 flex-1 flex-col transition-[padding] duration-300 ease-in-out motion-reduce:transition-none",
					isSidebarOpen && "lg:pr-[440px]",
				)}
			>
				<PageLayout
					header={
						<PageHeader
							type="asset"
							title={title}
							breadcrumbType="multiple"
							breadcrumbs={[
								{
									label: t("main.nav.items.records.label"),
									onPress: handleBreadcrumbRecords,
								},
								{
									label: recordNumberLabel,
									onPress: handleBreadcrumbRecord,
								},
								{
									label: t("tickets.landing.sections.assets"),
									onPress: handleBreadcrumbAssets,
								},
								{
									label: t(
										"main.jobs.workspace.assets.workspace.breadcrumb.current",
									),
									isCurrent: true,
								},
							]}
							metadata={metadata.length > 0 ? metadata : undefined}
							overflowAriaLabel={t("common.tableActions.options")}
							overflowActions={[]}
							actions={[
								{
									label: t("main.jobs.workspace.action"),
									"aria-label": t(
										"main.jobs.workspace.assets.workspace.collapse",
									),
									icon: Minimize01,
									color: "secondary",
									onPress: handleCollapseWorkspace,
								},
							]}
							showTabs
							tabs={tabs}
							selectedTabId={selectedTabId}
							onTabChange={handleTabChange}
						/>
					}
				>
					<Outlet />
				</PageLayout>
			</div>
			<Sidebar
				isOpen={isSidebarOpen}
				onOpenChange={setIsSidebarOpen}
				aria-label={sidebarLabel}
				className="max-lg:hidden"
			>
				<AssetEntityDetails
					orgId={params.orgId}
					assetId={params.assetId}
					surface={DetailsSurface.Tertiary}
					state={
						assetQuery.isPending ? DetailsState.Loading : DetailsState.Default
					}
					tabs={sidebarTabs}
					data-testid="asset-workspace-sidebar-details"
				/>
			</Sidebar>
		</div>
	);
}
